import React, { useState, useRef } from 'react';
import { 
  Modal, 
  Avatar, 
  Typography, 
  Button, 
  Tag, 
  Divider, 
  Row, 
  Col, 
  Upload,
  notification,
  Spin,
  Tabs,
  Grid
} from 'antd';
import { 
  UserOutlined, 
  MailOutlined, 
  ShopOutlined, 
  PhoneOutlined,
  EditOutlined,
  BankOutlined,
  GlobalOutlined,
  CalendarOutlined,
  IdcardOutlined,
  CrownOutlined,
  CameraOutlined,
  DeleteOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { ImageWithFallback } from 'components/shared/ImageWithFallBack';
import { useLanguage } from 'hooks/Languages/useLanguage';
import dayjs from 'dayjs';
import './styles.scss';
import endPoints from 'utils/endPoints';
import { useFetchUserPermission } from 'queries/auth/user';
import { FEATURE_KEYS } from 'utils/featureConstants';

const { Title, Text } = Typography;

// API base URL from environment - ensure trailing slash for proper concatenation
const baseURL = (() => {
  const url = process.env.REACT_APP_API_URL || '';
  return url.endsWith('/') ? url : url + '/';
})();

const mediaBaseURL = (() => {
  const url = process.env.REACT_APP_API_URL || '';
  return url.replace(/\/api\/?$/, '');
})();

const getFullUrl = (path: string) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${mediaBaseURL}${path.startsWith('/') ? '' : '/'}${path}`;
};

interface UserProfileModalProps {
  visible: boolean;
  onClose: () => void;
  userData: any;
  onRefresh?: () => void;
}

const UserProfileModal: React.FC<UserProfileModalProps> = ({
  visible,
  onClose,
  userData,
  onRefresh,
}) => {
  const { translate } = useLanguage();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const [isEditing, setIsEditing] = useState(false);
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const { userPermission } = useFetchUserPermission(userData?.user_role_id);

  const canEditBusiness = userData?.is_admin || 
    userPermission?.find((p: any) => p.feature_key === FEATURE_KEYS.SETTINGS)?.is_allowed;
  
  // Local state for preview
  const [profilePic, setProfilePic] = useState(userData?.picture_url);
  const [shopLogo, setShopLogo] = useState(userData?.shop_logo);
  const [profilePicError, setProfilePicError] = useState(false);
  const [shopLogoError, setShopLogoError] = useState(false);

  const getToken = () => localStorage.getItem('accessToken');

  const getSubscriptionColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'green';
      case 'trial':
        return 'blue';
      case 'expired':
        return 'red';
      default:
        return 'default';
    }
  };

  // Profile picture upload config
  const profileUploadProps: UploadProps = {
    name: 'file',
    action: `${baseURL}${endPoints.uploadProfilePicture}`,
    headers: {
      Authorization: `Bearer ${getToken()}`,
    },
    showUploadList: false,
    accept: 'image/jpeg,image/png,image/gif,image/webp',
    beforeUpload: (file) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        notification.error({
          message: translate('Error'),
          description: translate('You can only upload image files'),
          placement: 'topRight',
        });
        return false;
      }
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        notification.error({
          message: translate('Error'),
          description: translate('Image must be smaller than 5MB'),
          placement: 'topRight',
        });
        return false;
      }
      return true;
    },
    onChange: (info) => {
      if (info.file.status === 'uploading') {
        setUploadingProfile(true);
      }
      if (info.file.status === 'done') {
        setUploadingProfile(false);
        const response = info.file.response;
        console.log('Profile Upload Response:', response);
        const isSuccess = response?.success || 
                         (response?.message && response.message.toLowerCase().includes('successfully'));
        
        if (isSuccess) {
          setProfilePic(response.data?.picture_url);
          setProfilePicError(false);
          notification.success({
            message: translate('Success'),
            description: response?.message || translate('Profile picture updated successfully'),
            placement: 'topRight',
          });
          setTimeout(() => {
            onRefresh?.();
          }, 1500);
        } else {
          notification.error({
            message: translate('Error'),
            description: response?.message || translate('Upload failed'),
            placement: 'topRight',
          });
        }
      }
      if (info.file.status === 'error') {
        setUploadingProfile(false);
        notification.error({
          message: translate('Error'),
          description: translate('Upload failed'),
          placement: 'topRight',
        });
      }
    },
  };

  // Shop logo upload config  
  const logoUploadProps: UploadProps = {
    name: 'file',
    action: `${baseURL}${endPoints.uploadShopLogo}`,
    headers: {
      Authorization: `Bearer ${getToken()}`,
    },
    showUploadList: false,
    accept: 'image/jpeg,image/png,image/gif,image/webp',
    beforeUpload: (file) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        notification.error({
          message: translate('Error'),
          description: translate('You can only upload image files'),
          placement: 'topRight',
        });
        return false;
      }
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        notification.error({
          message: translate('Error'),
          description: translate('Image must be smaller than 5MB'),
          placement: 'topRight',
        });
        return false;
      }
      return true;
    },
    onChange: (info) => {
      if (info.file.status === 'uploading') {
        setUploadingLogo(true);
      }
      if (info.file.status === 'done') {
        setUploadingLogo(false);
        const response = info.file.response;
        console.log('Logo Upload Response:', response);
        const isSuccess = response?.success || 
                         (response?.message && response.message.toLowerCase().includes('successfully'));
        
        if (isSuccess) {
          setShopLogo(response.data?.logo_url);
          setShopLogoError(false);
          notification.success({
            message: translate('Success'),
            description: response?.message || translate('Business logo updated successfully'),
            placement: 'topRight',
          });
          setTimeout(() => {
            onRefresh?.();
          }, 1500);
        } else {
          notification.error({
            message: translate('Error'),
            description: response?.message || translate('Upload failed'),
            placement: 'topRight',
          });
        }
      }
      if (info.file.status === 'error') {
        setUploadingLogo(false);
        notification.error({
          message: translate('Error'),
          description: translate('Upload failed'),
          placement: 'topRight',
        });
      }
    },
  };

  const handleClose = () => {
    setIsEditing(false);
    setActiveTab('profile');
    onClose();
  };

  const renderProfileTab = () => (
    <>
      <div className="profile-header">
        <div className="avatar-upload-wrapper">
          <Avatar
            size={100}
            className="profile-avatar"
            src={profilePicError ? undefined : getFullUrl(profilePic)}
            icon={
              (profilePicError || !profilePic) && (
                <ImageWithFallback
                  src={getFullUrl(userData?.picture_url)}
                  firstName={userData?.first_name || userData?.username}
                  lastName={userData?.last_name || ''}
                />
              )
            }
            onError={() => {
              setProfilePicError(true);
              return true;
            }}
          />
          {isEditing && (
            <Upload {...profileUploadProps}>
              <Button 
                className="avatar-upload-btn"
                icon={uploadingProfile ? <Spin size="small" /> : <CameraOutlined />}
                shape="circle"
                size="small"
                disabled={uploadingProfile}
              />
            </Upload>
          )}
        </div>
        <div className="profile-name">
          {userData?.first_name || userData?.username} {userData?.last_name}
        </div>
        <div className="profile-email">{userData?.email}</div>
        <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
          <Tag className="profile-role-tag" style={{ margin: 0 }}>
            {translate(userData?.user_role || 'User')}
          </Tag>
          {userData?.subscription_status && (
            <Tag 
              color={getSubscriptionColor(userData.subscription_status)}
              style={{
                margin: 0,
                fontWeight: 600,
                padding: '4px 12px',
                borderRadius: '20px',
                textTransform: 'uppercase',
                border: 'none',
                height: 'auto',
                lineHeight: '1.5'
              }}
            >
              {translate(userData.subscription_status)}
            </Tag>
          )}
        </div>
      </div>

      <div className="profile-details">
        <Divider orientation="left" style={{ margin: '0 0 16px 0' }}>
          {translate('Personal Information')}
        </Divider>

        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <div className="detail-item">
              <div className="detail-icon">
                <UserOutlined />
              </div>
              <div className="detail-content">
                <div className="detail-label">{translate('Username')}</div>
                <div className="detail-value">{userData?.username}</div>
              </div>
            </div>
          </Col>

          <Col xs={24} md={12}>
            <div className="detail-item">
              <div className="detail-icon">
                <MailOutlined />
              </div>
              <div className="detail-content">
                <div className="detail-label">{translate('Email')}</div>
                <div className="detail-value">{userData?.email}</div>
              </div>
            </div>
          </Col>

          {userData?.phone_number && (
            <Col xs={24} md={12}>
              <div className="detail-item">
                <div className="detail-icon">
                  <PhoneOutlined />
                </div>
                <div className="detail-content">
                  <div className="detail-label">{translate('Phone')}</div>
                  <div className="detail-value">{userData.phone_number}</div>
                </div>
              </div>
            </Col>
          )}

          {userData?.id && (
            <Col xs={24} md={12}>
              <div className="detail-item">
                <div className="detail-icon">
                  <IdcardOutlined />
                </div>
                <div className="detail-content">
                  <div className="detail-label">{translate('User ID')}</div>
                  <div className="detail-value">#{userData.id}</div>
                </div>
              </div>
            </Col>
          )}
        </Row>
      </div>
    </>
  );

  const renderBusinessTab = () => (
    <div className="profile-details">
      {/* Business Logo Section */}
      <div className="business-logo-section">
        <div className="logo-upload-wrapper">
          {shopLogo && !shopLogoError ? (
            <img 
              src={getFullUrl(shopLogo)} 
              alt="Business Logo" 
              className="business-logo-preview"
              onError={() => setShopLogoError(true)}
            />
          ) : (
            <div className="logo-placeholder">
              <PictureOutlined style={{ fontSize: 32, color: '#94a3b8' }} />
              <span>{translate('No Logo')}</span>
            </div>
          )}
          {isEditing && canEditBusiness && (
            <Upload {...logoUploadProps}>
              <Button 
                className="logo-upload-btn"
                icon={uploadingLogo ? <Spin size="small" /> : <CameraOutlined />}
                disabled={uploadingLogo}
              >
                {translate('Change Logo')}
              </Button>
            </Upload>
          )}
        </div>
      </div>

      <Divider orientation="left" style={{ margin: '16px 0' }}>
        {translate('Business Information')}
      </Divider>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <div className="detail-item">
            <div className="detail-icon">
              <ShopOutlined />
            </div>
            <div className="detail-content">
              <div className="detail-label">{translate('Shop Name')}</div>
              <div className="detail-value">{userData?.shop_name || 'N/A'}</div>
            </div>
          </div>
        </Col>

        {userData?.business_type && (
          <Col xs={24} md={12}>
            <div className="detail-item">
              <div className="detail-icon">
                <BankOutlined />
              </div>
              <div className="detail-content">
                <div className="detail-label">{translate('Business Type')}</div>
                <div className="detail-value">
                  {userData.business_type.charAt(0).toUpperCase() + userData.business_type.slice(1)}
                </div>
              </div>
            </div>
          </Col>
        )}

        {userData?.branch_name && (
          <Col xs={24} md={12}>
            <div className="detail-item">
              <div className="detail-icon">
                <GlobalOutlined />
              </div>
              <div className="detail-content">
                <div className="detail-label">{translate('Branch')}</div>
                <div className="detail-value">{userData.branch_name}</div>
              </div>
            </div>
          </Col>
        )}

        {userData?.country && (
          <Col xs={24} md={12}>
            <div className="detail-item">
              <div className="detail-icon">
                <GlobalOutlined />
              </div>
              <div className="detail-content">
                <div className="detail-label">{translate('Country')}</div>
                <div className="detail-value">{userData.country}</div>
              </div>
            </div>
          </Col>
        )}
      </Row>

      {userData?.subscription_plan && (
        <>
          <Divider orientation="left" style={{ margin: '16px 0' }}>
            {translate('Subscription')}
          </Divider>

          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <div className="detail-item">
                <div className="detail-icon">
                  <CrownOutlined />
                </div>
                <div className="detail-content">
                  <div className="detail-label">{translate('Plan')}</div>
                  <div className="detail-value">{userData.subscription_plan}</div>
                </div>
              </div>
            </Col>

            {userData?.subscription_expiry && (
              <Col xs={24} md={12}>
                <div className="detail-item">
                  <div className="detail-icon">
                    <CalendarOutlined />
                  </div>
                  <div className="detail-content">
                    <div className="detail-label">{translate('Expiry Date')}</div>
                    <div className="detail-value">
                      {dayjs(userData.subscription_expiry).format('MMM DD, YYYY')}
                    </div>
                  </div>
                </div>
              </Col>
            )}
          </Row>
        </>
      )}

      {userData?.created_at && (
        <>
          <Divider orientation="left" style={{ margin: '16px 0' }}>
            {translate('Account Details')}
          </Divider>

          <Row gutter={[16, 16]}>
            <Col span={24}>
              <div className="detail-item">
                <div className="detail-icon">
                  <CalendarOutlined />
                </div>
                <div className="detail-content">
                  <div className="detail-label">{translate('Member Since')}</div>
                  <div className="detail-value">
                    {dayjs(userData.created_at).format('MMMM DD, YYYY')}
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </>
      )}
    </div>
  );

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={isMobile ? '100%' : 600}
      className={`user-profile-modal ${isMobile ? 'mobile-modal' : ''}`}
      style={{ top: isMobile ? 0 : undefined, margin: isMobile ? 0 : undefined, padding: isMobile ? 0 : undefined }}
      bodyStyle={{
        padding: isMobile ? '12px 12px 24px 12px' : undefined,
        height: isMobile ? '100vh' : undefined,
        overflowY: isMobile ? 'auto' : undefined,
      }}
      closeIcon={null}
      centered={!isMobile}
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        centered
        items={[
          {
            key: 'profile',
            label: translate('My Profile'),
            children: renderProfileTab(),
          },
          {
            key: 'business',
            label: translate('Business'),
            children: renderBusinessTab(),
          },
        ]}
      />

      <div className="profile-actions" style={{ marginTop: 24, display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        <Button size="large" onClick={handleClose} style={{ flex: isMobile ? 1 : undefined, height: 44, borderRadius: 12 }}>
          {translate('Close')}
        </Button>
        <Button 
          type="primary" 
          size="large" 
          icon={<EditOutlined />}
          onClick={() => setIsEditing(!isEditing)}
          style={{ flex: isMobile ? 1 : undefined, height: 44, borderRadius: 12, background: 'var(--primary-color)', borderColor: 'var(--primary-color)' }}
        >
          {isEditing ? translate('Done Editing') : translate('Edit Profile')}
        </Button>
      </div>
    </Modal>
  );
};

export default UserProfileModal;
